import { sessionAuthData } from '../../interfaces/states-data';

/**
 * Qué debe hacer ESTA pestaña ante un cambio de `sessionAuthState` hecho por OTRA.
 *
 *  - `logout`: la otra pestaña cerró sesión (borró la key o dejó `loggedIn:false`).
 *  - `sync`:   la otra pestaña rotó tokens o inició sesión; hay que adoptar ese
 *              estado en el store local para que no quede con tokens viejos.
 *  - `ignore`: nada que hacer (mismo token, JSON corrupto, estado incompleto).
 */
export type CrossTabAuthChange =
  | { kind: 'logout' }
  | { kind: 'sync'; state: sessionAuthData }
  | { kind: 'ignore' };

/**
 * Clasifica un cambio de `sessionAuthState` visto desde otra pestaña.
 *
 * @param latestRaw           valor ACTUAL de `localStorage.sessionAuthState`. Se
 *                            relee al manejar el evento en vez de usar
 *                            `event.newValue`: el evento `storage` llega en
 *                            diferido y la otra pestaña puede haber vuelto a
 *                            escribir entremedio. Adoptar `newValue` pisaría ese
 *                            estado más nuevo con uno intermedio.
 * @param currentAccessToken  access_token que tiene el store de ESTA pestaña.
 *
 * Antes solo se replicaba el logout. Las rotaciones de la otra pestaña se
 * ignoraban, así que el store local se quedaba con el par viejo para siempre; en
 * cuanto algo lo volvía a escribir en localStorage, la sesión moría en las dos
 * pestañas (ver `session-auth.reducers.ts`).
 */
export function classifyCrossTabAuthChange(
  latestRaw: string | null,
  currentAccessToken: string | null | undefined,
): CrossTabAuthChange {
  if (!latestRaw) {
    return { kind: 'logout' };
  }

  let latest: Partial<sessionAuthData> | null;
  try {
    latest = JSON.parse(latestRaw);
  } catch {
    return { kind: 'ignore' };
  }
  if (!latest || typeof latest !== 'object') {
    return { kind: 'ignore' };
  }

  if (latest.loggedIn === false) {
    return { kind: 'logout' };
  }

  if (latest.loggedIn !== true || !latest.access_token || !latest.refresh_token) {
    return { kind: 'ignore' };
  }

  if (latest.access_token === currentAccessToken) {
    return { kind: 'ignore' };
  }

  return { kind: 'sync', state: latest as sessionAuthData };
}
